import { ActionCreatorWithPayload, PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeEvery } from 'redux-saga/effects';

import {
  apiCopyCauseItem,
  apiDeleteCause,
  apiGetCauseItem,
  apiGetCausesList,
  apiReorderCauseList,
  apiSaveCauseItem,
} from 'src/api/causes';
import {
  apiCopyFaqItem,
  apiDeleteFaq,
  apiGetQuestionsItem,
  apiGetQuestionsList,
  apiReorderFaqList,
  apiSaveQuestionItem,
} from 'src/api/questions';
import { BaseApiErrorType, BaseApiResponseErrorsType } from 'src/models/BaseApi';
import { CauseItemResponseType, CauseItemType } from 'src/models/Causes';
import { FaqItemResponseType, FaqItemType } from 'src/models/DonationPage/Question';
import {
  CommonListCopyItemRequestType,
  CommonDeleteItemRequestType,
  CommonDeleteItemResponseType,
  CommonGetItemRequestType,
  CommonListCopyItemResponseType,
  CommonListRequestType,
  CommonListResponseType,
  CommonListType,
  CommonListItemType,
  CommonListReorderType,
  CommonListCopyItemReducerType,
  CommonListReorderResponseType,
} from 'src/models/listTypes';
import { donationPageSlice } from 'src/pages/DonationSetup/containers/slice';
import { baseSaga } from 'src/store/baseSaga';
import { httpResponseCheckSaga } from 'src/store/commonSaga';
import { SafeAnyType } from 'src/utils/safeAny';

/**
 * multiple action which need to be at the same place, because their logical path is identical
 *
 * there are some sagas which have a common logic
 * when you add a new consumer you must define some action and types for returning actions for reducer
 */

/** get list */
function* getListSaga({ payload, type }: PayloadAction<CommonListRequestType>) {
  let apiFn: (d: CommonListRequestType) => Promise<CommonListResponseType>;
  let okAction: ActionCreatorWithPayload<CommonListType>;
  let errorAction: ActionCreatorWithPayload<BaseApiResponseErrorsType | BaseApiErrorType>;

  switch (type) {
    case donationPageSlice.actions.getCausesListStart.type: {
      apiFn = apiGetCausesList;
      okAction = donationPageSlice.actions.getCausesListOk;
      errorAction = donationPageSlice.actions.getCausesListError;
      break;
    }
    case donationPageSlice.actions.getFaqListStart.type: {
      apiFn = apiGetQuestionsList;
      okAction = donationPageSlice.actions.getFaqListOk;
      errorAction = donationPageSlice.actions.getFaqListError;
      break;
    }
    default: {
      return;
    }
  }

  yield baseSaga(function* () {
    const response: CommonListResponseType = yield call(apiFn, payload);

    /** there is no invalid response */
    yield put(
      okAction({
        hasMore: !response.IsLastBatch,
        items: response.Items.map(({ ID, Title, Sequence, ImageMedia }) => ({
          id: ID,
          title: Title,
          sequence: Sequence,
          ImageMedia,
        })),
      })
    );
  }, errorAction);
}

/** get an item */
function* getItemSaga({ payload, type }: PayloadAction<CommonGetItemRequestType>): Generator {
  let apiFn: (d: CommonGetItemRequestType) => Promise<unknown>;
  let okAction: ActionCreatorWithPayload<SafeAnyType>;
  let errorAction: ActionCreatorWithPayload<BaseApiResponseErrorsType | BaseApiErrorType>;
  let fnToReducer: SafeAnyType;

  switch (type) {
    case donationPageSlice.actions.getCauseItemStart.type: {
      apiFn = apiGetCauseItem;
      okAction = donationPageSlice.actions.getCauseItemOk;
      errorAction = donationPageSlice.actions.getCauseItemError;
      fnToReducer = (response: CauseItemResponseType): CauseItemType => ({
        id: response.ID,
        description: response.Description,
        title: response.Title,
        imageUri: response.ImageMedias[0] ? response.ImageMedias[0].ImageUri || '' : '',
        sequence: response.Sequence,
      });
      break;
    }
    case donationPageSlice.actions.getFaqItemStart.type: {
      apiFn = apiGetQuestionsItem;
      okAction = donationPageSlice.actions.getFaqItemOk;
      errorAction = donationPageSlice.actions.getFaqItemError;
      fnToReducer = (response: FaqItemResponseType): FaqItemType => ({
        id: response.ID,
        description: response.Reply,
        title: response.Title,
        sequence: response.Sequence,
      });
      break;
    }
    default: {
      return;
    }
  }

  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiFn, payload);
    },
    function* (response) {
      yield put(okAction(fnToReducer(response)));
    },
    errorAction
  );
}

/** delete an item */
function* deleteItemSaga({ payload, type }: PayloadAction<CommonDeleteItemRequestType>): Generator {
  let apiFn: (d: CommonDeleteItemRequestType) => Promise<CommonDeleteItemResponseType>;
  let okAction: ActionCreatorWithPayload<CommonDeleteItemRequestType>;
  let errorAction: ActionCreatorWithPayload<BaseApiResponseErrorsType | BaseApiErrorType>;

  switch (type) {
    case donationPageSlice.actions.deleteCauseStart.type: {
      apiFn = apiDeleteCause;
      okAction = donationPageSlice.actions.deleteCauseOk;
      errorAction = donationPageSlice.actions.deleteCauseError;
      break;
    }
    case donationPageSlice.actions.deleteFaqStart.type: {
      apiFn = apiDeleteFaq;
      okAction = donationPageSlice.actions.deleteFaqOk;
      errorAction = donationPageSlice.actions.deleteFaqError;
      break;
    }
    default: {
      return;
    }
  }

  yield httpResponseCheckSaga<CommonDeleteItemResponseType>(
    function* () {
      return yield call(apiFn, payload);
    },
    function* () {
      yield put(okAction(payload));
    },
    errorAction
  );
}

/** copy an item */
function* copyItemSaga({ payload, type }: PayloadAction<CommonListCopyItemRequestType>): Generator {
  let apiFn: (d: CommonListCopyItemRequestType) => Promise<CommonListCopyItemResponseType>;
  let okAction: ActionCreatorWithPayload<CommonListItemType>;
  let errorAction: ActionCreatorWithPayload<BaseApiResponseErrorsType | BaseApiErrorType>;

  switch (type) {
    case donationPageSlice.actions.copyCauseStart.type: {
      apiFn = apiCopyCauseItem;
      okAction = donationPageSlice.actions.copyCauseOk;
      errorAction = donationPageSlice.actions.copyCauseError;
      break;
    }
    case donationPageSlice.actions.copyFaqStart.type: {
      apiFn = apiCopyFaqItem;
      okAction = donationPageSlice.actions.copyFaqOk;
      errorAction = donationPageSlice.actions.copyFaqError;
      break;
    }
    default: {
      return;
    }
  }

  yield httpResponseCheckSaga<CommonListCopyItemResponseType>(
    function* () {
      return yield call(apiFn, payload);
    },
    function* (response) {
      yield put(
        okAction({
          id: response.Result.ID,
          sequence: response.Result.Sequence,
          title: response.Result.Title,
        })
      );
    },
    errorAction
  );
}

/** reorder list */
function* reorderListSaga({ payload, type }: PayloadAction<CommonListReorderType>): Generator {
  let apiFn: (d: CommonListReorderType) => Promise<CommonListReorderResponseType>;
  let okAction: ActionCreatorWithPayload<SafeAnyType>;
  let errorAction: ActionCreatorWithPayload<CommonListCopyItemReducerType>;

  switch (type) {
    case donationPageSlice.actions.reorderCausesListStart.type: {
      apiFn = apiReorderCauseList;
      okAction = donationPageSlice.actions.reorderCausesListOk;
      errorAction = donationPageSlice.actions.reorderCausesListError;
      break;
    }
    case donationPageSlice.actions.reorderFaqListStart.type: {
      apiFn = apiReorderFaqList;
      okAction = donationPageSlice.actions.reorderFaqListOk;
      errorAction = donationPageSlice.actions.reorderFaqListError;
      break;
    }
    default: {
      return;
    }
  }

  yield httpResponseCheckSaga<CommonListReorderResponseType>(
    function* () {
      return yield call(apiFn, payload);
    },
    function* () {
      yield put(okAction(payload));
    },
    (e) => errorAction({ data: payload, error: e })
  );
}

/** save (create/update) an item */
function* saveItemSaga({ payload, type }: PayloadAction<SafeAnyType>) {
  let apiFn: (d: SafeAnyType) => Promise<SafeAnyType>;
  let okAction: ActionCreatorWithPayload<CommonListItemType>;
  let errorAction: ActionCreatorWithPayload<BaseApiResponseErrorsType | BaseApiErrorType>;

  switch (type) {
    case donationPageSlice.actions.saveCauseItemStart.type: {
      apiFn = apiSaveCauseItem;
      okAction = donationPageSlice.actions.saveCauseItemOk;
      errorAction = donationPageSlice.actions.saveCauseItemError;
      break;
    }
    case donationPageSlice.actions.saveFaqItemStart.type: {
      apiFn = apiSaveQuestionItem;
      okAction = donationPageSlice.actions.saveFaqItemOk;
      errorAction = donationPageSlice.actions.saveFaqItemError;
      break;
    }
    default: {
      return;
    }
  }

  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiFn, payload);
    },
    function* (response) {
      yield put(
        okAction({
          id: response.ID,
          sequence: response.Sequence,
          title: response.Title,
          ImageMedia: response.ImageMedia,
        })
      );
    },
    errorAction
  );
}

export function* listItemsSagas(): Generator {
  yield takeEvery(
    [donationPageSlice.actions.getCausesListStart.type, donationPageSlice.actions.getFaqListStart.type],
    getListSaga
  );

  yield takeEvery(
    [donationPageSlice.actions.getCauseItemStart.type, donationPageSlice.actions.getFaqItemStart.type],
    getItemSaga
  );

  yield takeEvery(
    [donationPageSlice.actions.deleteCauseStart.type, donationPageSlice.actions.deleteFaqStart.type],
    deleteItemSaga
  );

  yield takeEvery(
    [donationPageSlice.actions.copyCauseStart.type, donationPageSlice.actions.copyFaqStart.type],
    copyItemSaga
  );

  yield takeEvery(
    [donationPageSlice.actions.reorderCausesListStart.type, donationPageSlice.actions.reorderFaqListStart.type],
    reorderListSaga
  );

  yield takeEvery(
    [donationPageSlice.actions.saveCauseItemStart.type, donationPageSlice.actions.saveFaqItemStart.type],
    saveItemSaga
  );
}
